/**
 * Audit Thank You — Post-purchase confirmation page
 *
 * Users land here after a successful $397 Stripe payment for the
 * Private AI Infrastructure Audit.
 *
 * Scheduling integration placeholder:
 *   Replace <SchedulingPlaceholder /> with your calendar embed
 *   (Calendly, Cal.com, SavvyCal, etc.) when ready.
 *   The surrounding layout and copy stay unchanged.
 */

import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle, ArrowRight, Loader2, Lock } from "lucide-react";
import Aurora from "@/components/Aurora";
import { track } from "@/lib/analytics";
import { getAuditStatus } from "@/lib/api";

// ─── Calendly inline widget ────────────────────────────────────────────────────
const CALENDLY_URL =
  "https://calendly.com/contact-favorstudio/private-ai-infrastructure-audit";

function CalendlyWidget() {
  useEffect(() => {
    // Inject Calendly script once
    if (document.getElementById("calendly-script")) return;
    const script = document.createElement("script");
    script.id = "calendly-script";
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.head.appendChild(script);
  }, []);

  return (
    <div
      className="calendly-inline-widget w-full rounded-2xl overflow-hidden"
      data-url={CALENDLY_URL}
      style={{ minWidth: 320, height: 700 }}
    />
  );
}
// ─────────────────────────────────────────────────────────────────────────────

const CHECKLIST = [
  "Analyze your biggest business constraint",
  "Recommend the highest-impact AI Employees",
  "Build your AI Infrastructure roadmap",
  "Create a practical implementation strategy",
];

export default function AuditThankYou() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [gate, setGate] = useState(() => (sessionId ? "checking" : "denied"));

  // SECURITY FIX: this page used to render the Calendly scheduler unconditionally
  // on mount — anyone with the URL got a free booking slot. It now requires a
  // verified, paid audit checkout session before anything scheduling-related shows.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    getAuditStatus(sessionId)
      .then((resp) => {
        if (cancelled) return;
        setGate(resp.paid ? "ok" : "denied");
      })
      .catch(() => {
        if (!cancelled) setGate("denied");
      });
    return () => { cancelled = true; };
  }, [sessionId]);

  // Fire analytics once on mount
  React.useEffect(() => {
    track("audit_thank_you_viewed");
  }, []);

  function handleBookClick() {
    track("book_infrastructure_audit_clicked", { source: "thank_you_page" });
    // Scroll to scheduling section
    document.getElementById("schedule")?.scrollIntoView({ behavior: "smooth" });
  }

  if (gate === "checking") {
    return (
      <div className="min-h-screen bg-black text-white relative overflow-hidden grid place-items-center">
        <Aurora variant="intense" />
        <div className="relative flex items-center gap-3 text-zinc-400">
          <Loader2 className="w-5 h-5 animate-spin text-cyan-300" />
          <span className="text-sm">Verifying your purchase…</span>
        </div>
      </div>
    );
  }

  if (gate === "denied") {
    return (
      <div className="min-h-screen bg-black text-white relative overflow-hidden grid place-items-center px-5">
        <Aurora variant="intense" />
        <div className="relative max-w-md text-center">
          <Lock className="w-8 h-8 text-rose-300 mx-auto mb-4" />
          <h1 className="font-display text-2xl mb-3">This page requires a completed audit purchase.</h1>
          <p className="text-zinc-400 text-sm">
            If you just paid, this can take a few seconds to confirm — try refreshing. Otherwise, questions?
            Email us at{" "}
            <a href="mailto:contact@favorstudio.agency" className="text-cyan-300 hover:underline">
              contact@favorstudio.agency
            </a>
            .
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <Aurora variant="intense" />

      <div className="relative max-w-2xl mx-auto px-5 sm:px-8 py-16 sm:py-24">

        {/* Logo */}
        <div className="flex items-center gap-2 mb-14">
          <span
            className="inline-flex w-9 h-9 rounded-xl items-center justify-center shadow-[0_0_24px_rgba(0,229,255,0.5)]"
            style={{ background: "linear-gradient(135deg, #67e8f9, #2563eb)" }}
          >
            <span className="text-black font-bold text-xs">FS</span>
          </span>
          <span className="font-display text-white text-lg">
            <span className="font-normal">Favor</span>{" "}
            <span className="font-bold">Studio.</span>
          </span>
        </div>

        {/* Success badge */}
        <div
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-8"
          style={{ border: "1px solid rgba(0,229,255,0.3)", background: "rgba(0,229,255,0.07)" }}
        >
          <CheckCircle size={13} color="#00E5FF" />
          <span className="text-[10px] tracking-[0.25em] text-sky-300 font-semibold uppercase" style={{ fontFamily: "Manrope, sans-serif" }}>
            Audit Reserved
          </span>
        </div>

        {/* Headline */}
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05] text-white mb-4">
          <span className="font-normal">Welcome to Your </span>
          <span className="font-bold">AI Infrastructure Audit</span>
        </h1>

        {/* Subheadline */}
        <p className="text-zinc-300 text-lg font-semibold mb-5" style={{ fontFamily: "Manrope, sans-serif" }}>
          Congratulations. Your Infrastructure Audit has been reserved.
        </p>

        {/* Body copy */}
        <p className="text-zinc-400 text-base leading-relaxed mb-12" style={{ fontFamily: "Manrope, sans-serif" }}>
          Over the next 60–90 minutes we'll identify the biggest constraint inside your business
          and design a custom AI Infrastructure roadmap built specifically for your business.
        </p>

        {/* Divider */}
        <div className="h-px bg-white/5 mb-12" />

        {/* What Happens Next */}
        <div className="mb-12">
          <h2 className="font-display text-xl font-bold tracking-tight text-white mb-7">
            What Happens Next
          </h2>

          <div className="space-y-4">
            {CHECKLIST.map((item, i) => (
              <div key={i} className="flex items-start gap-4">
                <span
                  className="flex-shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center"
                  style={{
                    background: "rgba(0,240,255,0.12)",
                    border: "1px solid rgba(0,240,255,0.5)",
                  }}
                >
                  <svg width="10" height="9" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4L3.5 6.5L9 1" stroke="#00F0FF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="text-zinc-200 text-base leading-relaxed" style={{ fontFamily: "Manrope, sans-serif" }}>
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Primary CTA */}
        <button
          onClick={handleBookClick}
          className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-sm tracking-wider transition-all mb-12"
          style={{
            background: "linear-gradient(135deg, #00E5FF 0%, #0066FF 100%)",
            border: "1px solid rgba(0,229,255,0.5)",
            color: "#001018",
            cursor: "pointer",
            fontFamily: "Manrope, sans-serif",
          }}
        >
          BOOK MY INFRASTRUCTURE AUDIT <ArrowRight size={15} />
        </button>

        {/* Scheduling section */}
        <div id="schedule">
          <h3 className="font-display text-lg font-semibold text-white mb-5">
            Select Your Session Time
          </h3>
          <CalendlyWidget />
        </div>

        <p className="mt-8 text-xs text-zinc-600 text-center" style={{ fontFamily: "Manrope, sans-serif" }}>
          Questions? Email us at{" "}
          <a href="mailto:contact@favorstudio.agency" className="text-zinc-400 hover:text-cyan-300 transition-colors">
            contact@favorstudio.agency
          </a>
        </p>

      </div>
    </div>
  );
}
